import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import moment from 'moment-timezone';
import Numeral from 'numeral'; // Lodash

import _invert from 'lodash/invert';
import _padEnd from 'lodash/padEnd';
import _toNumber from 'lodash/toNumber';
import _curry from 'lodash/curry';
import _isNaN from 'lodash/isNaN';
import _parseInt from 'lodash/parseInt';
import _flow from 'lodash/flow';
import _repeat from 'lodash/repeat';
import _join from 'lodash/join';
import _round from 'lodash/round';
import _reduce from 'lodash/reduce';
import _trim from 'lodash/trim';
import _toInteger from 'lodash/toInteger';
import _isNumber from 'lodash/isNumber';
import _isString from 'lodash/isString';
import _isNil from 'lodash/isNil'; // Helpers

import { parseNumberFromText } from './parsers';
import mathUtils from './math';
import { roundNumberToNearestMultiple } from './numberUtils'; // Constants

import { EMPTY_OBJECT, EMPTY_STRING } from '../app.constants';
/**
 * 18th July 2018 5:38 PM representations -
 * moment().format('LT')    // 8:33 AM
 * moment().format('LTS')   // 8:33:02 AM
 * moment().format('L')     // 07/18/2018
 * moment().format('l')     // 7/18/2018
 * moment().format('LL')    // July 18, 2018
 * moment().format('ll')    // Jul 18, 2018
 * moment().format('LLL')   // July 18, 2018 8:33 AM
 * moment().format('lll')   // Jul 18, 2018 8:33 AM
 * moment().format('LLLL')  // Wednesday, July 18, 2018 8:33 AM
 * moment().format('llll')  // Wed, Jul 18, 2018 8:34 AM
 * moment().format('ddd ll')// Wed Jul 18, 2018
 * moment().format('x')     // 15489593990000 in msec
 * moment().format('YYYY-MM-DD hh:mm:ss -0500')     // 2019-09-26 02:33:32 -0500
 * moment().format('MM/YYYY) // 07/2018
 * moment().format('YYYYMMDD) // 20180718
 * moment().format('YYYY_MM) // 2018_07
 * moment().format('YYYY) // 2020
 * moment().format('MMMM) // January
 * * moment().format('MMM D') // Jan 21
 * moment().format('MMM DD, LT') // Feb 21, 2:31 AM
 * moment().format('ddd, MMM DD') // Mon, Jul 02
 * moment().format('dddd - DD MMMM, YYYY) // Thursday - 27 August, 2020
 * moment().format('ddd D') // Mon 1
 * moment().format('ddd') // Mon
 * moment().format('dddd') // Monday
 * moment().format('h') // 6 // hours
 * moment().format('ha') // 2 pm
 * moment().format('ddd, MMM DD, YYYY) // Mon, Jan 18, 2021
 * moment().format('ddd, Do MMM') // Mon, 18th Jan
 * moment().format('D') // 18
 * moment().format('ll | LT') // Aug 11, 2021 | 6:48 PM
 * moment().format('D MMM YYYY, h:mm a'); // 9 Sep 2021, 12:21 pm
 * */

export var DATE_TIME_FORMATS = {
  ABBREVIATED_MONTH_COMMA_FULL_YEAR: 'MMM, YYYY',
  HOUR_MIN: 'LT',
  HOUR_MIN_SEC: 'LTS',
  NUMERIC_DATE: 'L',
  ABBREVIATED_NUMERIC_DATE: 'l',
  DATE_ALPHA_MONTH: 'LL',
  ABBREVIATED_DATE_ALPHA_MONTH: 'll',
  DATE_ALPHA_MONTH_WITH_TIME: 'LLL',
  ABBREVIATED_DATE_ALPHA_MONTH_WITH_TIME: 'lll',
  DATE_ALPHA_WITH_DAY: 'LLLL',
  ABBREVIATED_DATE_ALPHA_WITH_DAY: 'llll',
  NATIVE_DATE_FORMAT: 'DD-MM-YYYY',
  ABBREVIATED_BASE_DATE_FORMAT: 'DD/MM/YY',
  BASE_DATE_FORMAT_US: 'MM/DD/YYYY',
  ABBREVIATED_BASE_DATE_FORMAT_US: 'MM/DD/YY',
  DATE_WITH_DAY: 'ddd ll',
  DATE_WITH_FULL_DAY: 'dddd ll',
  DATE_WITH_TIME: 'd-MMM-YYYY h:m:s A',
  ISO_8601_DATE: 'YYYY-MM-DD',
  TIME_STAMP: 'x',
  HOUR_MIN_FORMAT: 'hh:mm A',
  HOUR_MIN_FORMAT_WITHOUT_ZERO: 'h:mm A',
  HOUR_MIN_FORMAT_WITHOUT_SUFFIX: 'hh:mm',
  TWENTY_FOUR_HOUR_MIN: 'HH:mm',
  HOUR_MIN_SEC_FORMAT: 'HH:mm:ss A',
  TWENTY_FOUR_HOUR_MIN_SEC_FORMAT_WITHOUT_SUFFIX: 'HH:mm:ss',
  DEAL_REBATE_EST: 'YYYY-MM-DD hh:mm:ss -0500',
  MONTH_YEAR: 'MM/YYYY',
  DOWNLOADABLE_DATE_TIME_FORMAT: 'DD-MMM-YYYY',
  MONTH_YEAR_FORMAT: 'MMM YYYY',
  FULL_MONTH_YEAR_FORMAT: 'MMMM YYYY',
  EXPORT_EXCEL_FORMAT: 'YYYYMMDD',
  YEAR_MONTH: 'YYYY_MM',
  YEAR: 'YYYY',
  MONTH: 'MMMM',
  MONTH_DAY: 'MMM D',
  DAY_MONTH: 'DD-MMM',
  ABBREVIATED_MONTH_WITH_TIME: 'MMM DD, LT',
  ABBREVIATED_MONTH_DATE_YEAR: 'MMM DD, YYYY',
  FULL_MONTH_DATE_YEAR: 'MMMM DD, YYYY',
  YEAR_MONTH_DATE_TIME: 'DD MMMM, YYYY [at] h:m A',
  ABBREVIATED_MONTH_DAY_COMMA_FULL_YEAR: 'MMM DD, YYYY',
  MONTH_DATE_YEAR_HYPHEN: 'MMM DD, YYYY | hh:mm a',
  DAY_MONTH_DATE_YEAR_TIME: 'ddd, MMM DD, YYYY | hh:mm a',
  DAY_MONTH_DATE_YEAR_HOUR_MIN_SECONDS: 'ddd, MMM DD, YYYY | hh:mm:ss A',
  DAY_MONTH_DATE_YEAR_WITH_TIME: 'ddd MMM DD, YYYY | hh:mmA',
  DATE_MONTH_WITH_DAY: 'ddd, MMM DD',
  HOUR_MIN_SEC_WITH_LETTER: 'h[h] mm[m] ss[s]',
  ABBREVIATED_BASE_DATE_FORMAT_US_WITH_TIME: 'MM/DD/YY | hh:mm A',
  ABBREVIATED_BASE_DATE_FORMAT_US_WITH_24_HOUR_TIME: 'MM/DD/YYYY HH:mm A',
  TIME_WITH_DATE_FORMAT: 'hh:mm A | MMM DD, YYYY',
  DAY_DATE_MONTH_YEAR: 'dddd - DD MMMM, YYYY',
  ABBREVIATED_DAY_NAME: 'dd',
  FULL_DAY_NAME: 'dddd',
  DAY_MONTH_DATE_WITH_TIME: 'ddd MMM DD | hh:mmA',
  DOWNLOADABLE_DATE_FORMAT_WITH_TIME: 'DD-MMM-YYYY hh:mm:ss A',
  ABBREVIATED_DATE_MONTH_YEAR_WITH_TIME: 'Do MMM, YYYY | hh:mm A',
  SHORT_WEEK_WITH_DAY: 'ddd D',
  SHORT_WEEK: 'ddd',
  SHORT_HOUR: 'h',
  SHORT_MINUTE: 'm',
  AM_PM: 'A',
  HOUR_MIN_SHORT: 'ha',
  MONTH_DAY_FULL: 'MMMM DD',
  DAY_YEAR: 'DD, YYYY',
  HOUR_MIN_WITHOUT_MERIDIAN: 'h:mm',
  HOUR_MIN_WITH_MERIDIAN: 'h:mma',
  DATE_MONTH_YEAR: 'DD MMM YYYY',
  DATE_MONTH_COMMA_YEAR: 'DD MMM, YYYY',
  DAY_MONTH_DATE_YEAR: 'ddd, MMM DD, YYYY',
  DATE_WEEK_YEAR: 'ww - YYYY',
  QUARTER_YEAR: '[Q]Q - YYYY',
  DATE_ORDINAL_DAYS_ALPHA_MONTH_YEAR: 'Do MMMM, YYYY',
  MONTH_DATE_YEAR_TIME: 'MMM DD, YYYY | hh:mm A',
  MONTH_DATE_TIME: 'MMM DD HH:mm A',
  DATE_ORDINAL_SHORT_DAY_WITH_MONTH: 'ddd, Do MMM',
  DAY_OF_MONTH: 'D',
  DATE_MONTH_YEAR_WITH_TIME_IN_24HR_FORMAT: 'DD MMM YYYY, HH:mm',
  ABBREVIATED_DATE_ALPHA_MONTH_YEAR_WITH_TIME: 'll | LT',
  ABBREVIATED_MONTH_DATE: 'MM/DD',
  ABBREVIATED_MONTH: 'MMM',
  MONTH_HALF_YEAR: 'MMM YY',
  ABBREVIATED_DATE_MONTH_YEAR_WITH_TIME_MERIDIAN: 'D MMM YYYY, h:mm a',
  ABBREVIATED_DATE_MONTH_WITH_TIME_MERIDIAN: 'D MMM LT',
  ABBREVIATED_MONTH_DATE_YEAR_WITH_TIME_MERIDIAN: 'MMM D YYYY, h:mm a',
  MONTH_OF_YEAR: 'M',
  HOUR_MIN_SEC_12HRS: 'hh:mm:ss A',
  MONTH_DATE_YEAR_TIME_12HR_FORMAT: 'MMMM D, YYYY hh:mm A',
  // OCT 22, 2021 02:34PM
  TIME_IN_HOURS: 'HH',
  TIME_IN_MINUTES: 'mm',
  DATE_ORDINAL_MONTH_YEAR_WITH_TIME: 'Do MMM YYYY, hh:mm A',
  YEAR_MONETH_DATE_TIME: 'YYYY-DD-MM_HHmm',
  MONTH_DATE: 'MMDD',
  MONTH_DATE_YEAR: 'MMDDYYYY',
  HOUR_MINUTE_SECOND: 'HHmmss',
  MONTH_DATE_YEAR_TIME_WITH_AT: 'MMM DD, YYYY [at] hh:mm a',
  FULL_MONTH_YEAR: 'MMMM, YYYY',
  DAY_MONTH_DATE_YEAR_TIME_FORMAT: 'ddd, MMM DD, YYYY | hh:mm A'
};
export var DAY_VS_DOW = {
  MONDAY: 1,
  TUESDAY: 2,
  WEDNESDAY: 3,
  THURSDAY: 4,
  FRIDAY: 5,
  SATURDAY: 6,
  SUNDAY: 7
};

var MOMENT_VS_TEKION_FORMAT = _invert(DATE_TIME_FORMATS);

export var momentWithTimeZone = function momentWithTimeZone(timeZone) {
  return moment().tz(timeZone);
};
export var setGlobalTimeZone = function setGlobalTimeZone(timeZone) {
  return moment.tz.setDefault(timeZone);
};
export var setGlobalLocale = function setGlobalLocale() {
  var locale = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'en';
  var additional = arguments.length > 1 ? arguments[1] : undefined;
  return moment.locale(locale, additional);
};

var isValidDateTimeFormat = function isValidDateTimeFormat(format) {
  return MOMENT_VS_TEKION_FORMAT[format];
};
/**
 * Format given timestamp according to the format passed
 * @param  {[type]} epochOrMomentObject Timestamp
 * @param  {[type]} format   One of the format mentioned in DATE_TIME_FORMATS
 * @param  {[type]} timezone Timezone in which to format the date
 * @return {[type]}          Formatted date/time
 */


export var getFormattedDateTime = function getFormattedDateTime(epochOrMomentObject, format, timezone) {
  if (!isValidDateTimeFormat(format)) {
    throw "Invalid date format :- ".concat(format); // eslint-disable-line no-throw-literal
  }

  var momentObject = moment(epochOrMomentObject);
  var momentToFormat = timezone ? momentObject.tz(timezone) : momentObject;
  return momentToFormat.format(format);
};
export var isEmptyDate = function isEmptyDate(timeStamp) {
  return timeStamp ? getFormattedDateTime(timeStamp, DATE_TIME_FORMATS.ABBREVIATED_DATE_ALPHA_MONTH) : undefined;
};
/**
 * get a moment object of current time
 */

export var getCurrentTime = function getCurrentTime() {
  return moment();
};
export var getCurrentNearestQuarterInMinutes = function getCurrentNearestQuarterInMinutes() {
  var roundedMinutes = Math.ceil(moment().minute() / 15) * 15;
  return moment().clone().minute(roundedMinutes);
};
/**
 * get yesterday moment object
 */

export var getYesterdayTime = function getYesterdayTime() {
  return moment().subtract(1, 'days');
};
/**
 * get a moment object for passed in date
 * @param {any} date can be a momentObject or a string
 * @returns a moment Object
 */

export var toMoment = function toMoment(date) {
  var momentObj = moment(date);
  if (momentObj.isValid()) return momentObj;
  return undefined;
};
/*
 * to get short week days range
 * e.g [mon, tue, ...., sun]
 * @returns week days array
 */

export var getWeekdaysShort = function getWeekdaysShort() {
  return moment.weekdaysShort();
};
/*
 * to get short month name range
 * e.g [jan, feb, ...., dec]
 * @returns month days array
 */

export var getMonthsShort = function getMonthsShort() {
  return moment.monthsShort();
};
export var toMomentWithParserFormat = function toMomentWithParserFormat(date) {
  var format = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : DATE_TIME_FORMATS.TIME_STAMP;
  var momentObj = moment(date, format);
  if (momentObj.isValid()) return momentObj;
  return undefined;
};
/**
 * get a moment object from passed in unix timeStamp
 * @param {any} timeStamp is a number
 * @returns a moment Object
 */

export var toMomentFromUnixTimeStamp = function toMomentFromUnixTimeStamp(timeStamp) {
  return moment.unix(timeStamp);
};
/**
 * returns the moment object for the current date
 * @return {object}
 */

export var getToday = function getToday() {
  return toMoment().startOf('day');
};
/**
 * @param {Number} month Where January is 0 and December is 11
 * @param {Number} year
 * @returns {Moment}
 */

export var getMonthOfYearMoment = function getMonthOfYearMoment(month, year) {
  return toMoment().year(year).month(month);
};
/**
 * returns the moment object for the end of the day today
 * @return {object}
 */

export var getEndOfToday = function getEndOfToday() {
  return toMoment().endOf('day');
};
/**
 * check if two days are the same day
 * @param {any} dayOne can be a momentObject or a string
 * @param {any} dayTwo can be a momentObject or a string
 * @return {boolean}
 */

export var isSameDay = function isSameDay(dayOne, dayTwo) {
  return toMoment(dayOne).isSame(dayTwo, 'day');
};
/**
 * check if two minutes are the same
 * @param {any} minuteOne can be a momentObject or a string
 * @param {any} minuteTwo can be a momentObject or a string
 * @return {boolean}
 */

export var isSameMinute = function isSameMinute(minuteOne, minuteTwo) {
  return toMoment(minuteOne).isSame(minuteTwo, 'minute');
};
/**
 * returns the utc string format for a given date
 * @param {any} date can be a momentObject or a string
 * @return {string}
 */

export var getDateAsUTCString = function getDateAsUTCString(date) {
  return toMoment(date).utc().format();
};
export var getUnix = function getUnix(date) {
  return toMoment(date).valueOf();
};
/**
 * Adds the provided numberOfdays to the provided date or defaults to today.
 * @param {any} numberOfDaysToAdd number of days to add
 * @param {any} startDate can be a momentObject or a string
 * @returns a moment object
 */

export var addDays = function addDays(numberOfDaysToAdd, startDate) {
  var start = startDate && toMoment(startDate) || getToday();
  return start.add(numberOfDaysToAdd, 'days');
};
/**
 * checks if a date is valid or not
 * @param {any} date can be a momentObject or a string
 * @returns a boolean
 */

export var isValidDate = function isValidDate(date) {
  var momentInstance = toMoment(date);
  return momentInstance ? toMoment(date).isValid() : false;
};
export var getDifferenceAsDays = function getDifferenceAsDays(end, start) {
  var duration = moment.duration(moment(end).startOf('day').diff(moment(start).startOf('day')));
  return Math.round(duration.asDays());
};
export var getDifferenceOfDateRangeAsDays = function getDifferenceOfDateRangeAsDays(start, end) {
  var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {
    round: true
  };

  var _ref = options || EMPTY_OBJECT,
      timeZone = _ref.timeZone,
      round = _ref.round;

  var startDate = timeZone ? getTimeWithTimeZone(timeZone, start) : moment(start);
  var endDate = timeZone ? getTimeWithTimeZone(timeZone, end) : moment(end);
  var duration = moment.duration(endDate.endOf('day').diff(startDate.startOf('day')));
  return round ? Math.round(duration.asDays()) : duration.asDays();
};
export var getAbsoluteDifferenceAsMonths = function getAbsoluteDifferenceAsMonths(start) {
  var end = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : moment();
  var duration = moment.duration(moment(end).diff(moment(start)));
  return Math.round(duration.asMonths());
};
export var getDifferenceAsMonths = function getDifferenceAsMonths(start) {
  var end = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : moment();
  var duration = moment.duration(moment(end).diff(moment(start)));
  return duration.asMonths();
};
export var getAbsoluteDifferenceAsDays = function getAbsoluteDifferenceAsDays(start) {
  var end = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : moment();
  var duration = moment.duration(moment(end).diff(moment(start)));
  return duration.asDays();
};
export var getDifferenceAsMinutes = function getDifferenceAsMinutes(end, start) {
  var duration = moment.duration(moment(end).diff(moment(start)));
  return Math.round(duration.asMinutes());
};
export var getDifferenceAsSeconds = function getDifferenceAsSeconds(end, start) {
  var duration = moment.duration(moment(end).diff(moment(start)));
  return duration.asSeconds();
};
export var getDifferenceAsHours = function getDifferenceAsHours(end, start) {
  var duration = moment.duration(moment(end).diff(moment(start)));
  return duration.asHours();
};
export var getDifferenceAsHoursFromToday = function getDifferenceAsHoursFromToday(start) {
  var end = getTodayTimeStamp();
  return getDifferenceAsHours(end, start);
};
export var getDifferenceAsMinutesFromToday = function getDifferenceAsMinutesFromToday(start) {
  var end = getTodayTimeStamp();
  return getDifferenceAsMinutes(end, start);
};
export var getDifferenceAsDaysFromToday = function getDifferenceAsDaysFromToday(start) {
  var end = getToday();
  return getDifferenceAsDays(end, start);
};
/**
 * returns the default date string format
 * @param {any} date can be a momentObject or a string
 * @returns a {string}
 */

export var getDefaultDateFormat = function getDefaultDateFormat(date) {
  return getFormattedDateTime(date, DATE_TIME_FORMATS.DATE_WITH_DAY);
};
/**
 * returns the number of milliseconds since the Epoch
 * @param {moment} date momentObject
 * @returns {any} string
 */

export var getMomentValueOf = function getMomentValueOf(date) {
  return moment(date).valueOf();
};
export var getTimeStamp = function getTimeStamp(date) {
  return moment(date).format('x');
};
export var getMomentObject = function getMomentObject(date) {
  return moment(date);
};
/**
 * getter and setter (date to a particular day, month)
 */

export var getDayOfMonth = function getDayOfMonth() {
  var date = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : moment();
  return moment(date).date();
};
export var setDayOfMonth = function setDayOfMonth(dayOfMonth) {
  var date = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : moment();
  return moment(date).date(dayOfMonth);
};
export var getMonth = function getMonth() {
  var date = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : moment();
  return moment(date).month();
};
export var setMonth = function setMonth(monthOfYear) {
  var date = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : moment();
  return moment(date).month(monthOfYear);
};
/**
 * returns start day of month
 * @returns a {string}
 */

export var getMonthStartDay = function getMonthStartDay(format, timeZone) {
  var momentValueWithTZ = momentWithTimeZone(timeZone);
  return timeZone ? momentValueWithTZ.startOf('month').format(format) : moment().startOf('month').format(format);
};
/**
 * returns end day of month
 * @returns a {string}
 */

export var getMonthEndDay = function getMonthEndDay(format, timeZone) {
  var momentValueWithTZ = momentWithTimeZone(timeZone);
  return timeZone ? momentValueWithTZ.endOf('month').format(format) : moment().endOf('month').format(format);
};
/**
 * returns epoch value for start of the month
 */

export var getStartOfMonthEpoch = function getStartOfMonthEpoch(month, year) {
  return +toMoment().year(year).month(month).startOf('month');
};
/**
 * returns epoch value for end of the month
 */

export var getEndOfMonthEpoch = function getEndOfMonthEpoch(month, year) {
  return +toMoment().year(year).month(month).endOf('month');
};
/**
 * returns today in unix timestamp
 * @returns a {string}
 */

export var getTodayTimeStamp = function getTodayTimeStamp() {
  return parseNumberFromText(moment().format('x'));
};
/**
 * returns startOfDay/endOfDay in unix timestamp
 */

export var startOfDay = function startOfDay(time) {
  return moment(time).startOf('day');
};
export var endOfDay = function endOfDay(time) {
  return moment(time).endOf('day');
};
export var getTimeWithTimeZone = function getTimeWithTimeZone(timeZone, time) {
  return timeZone ? moment(time).tz(timeZone) : moment(time);
};
export var humanize = function humanize(duration) {
  var durationUnit = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'minutes';
  var addSuffix = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
  return moment.duration(duration, durationUnit, addSuffix).humanize();
};
export var isSame = function isSame(date1, date2) {
  var _ref2 = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : EMPTY_OBJECT,
      _ref2$unit = _ref2.unit,
      unit = _ref2$unit === void 0 ? 'day' : _ref2$unit;

  return toMoment(date2).isSame(date1, unit);
};
export var isAfter = function isAfter(date1, date2) {
  var _ref3 = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : EMPTY_OBJECT,
      _ref3$unit = _ref3.unit,
      unit = _ref3$unit === void 0 ? 'day' : _ref3$unit,
      _ref3$inclusive = _ref3.inclusive,
      inclusive = _ref3$inclusive === void 0 ? false : _ref3$inclusive;

  if (inclusive) return toMoment(date2).isSameOrAfter(date1, unit);
  return toMoment(date2).isAfter(date1, unit);
};
/**
 * Returns `true` if `date2` is before `date1`
 * @param {moment.Moment} date1
 * @param {moment.Moment} date2
 * @param {{unit: moment.unitOfTime.StartOf, inclusive: boolean}} [options]
 * @returns
 */

export var isBefore = function isBefore(date1, date2) {
  var _ref4 = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : EMPTY_OBJECT,
      _ref4$unit = _ref4.unit,
      unit = _ref4$unit === void 0 ? 'day' : _ref4$unit,
      _ref4$inclusive = _ref4.inclusive,
      inclusive = _ref4$inclusive === void 0 ? false : _ref4$inclusive;

  if (inclusive) return toMoment(date2).isSameOrBefore(date1, unit);
  return toMoment(date2).isBefore(date1, unit);
};
export var ONE_HOUR_TO_SECONDS = 3600;
export var ONE_MINUTE_TO_SECONDS = 60;
export var ONE_HOUR_TO_MINUTES = 60;
export var ONE_SECOND_TO_MILLISECONDS = 1000;
var ROUND_OFF_PRECISION = 0.00111111;
var DEFAULT_ROUND_OFF_THRESHOLD = 5 * ROUND_OFF_PRECISION;
export var DECIMAL_FIXED_VALUE_TIME = 2;
var DECIMAL_FIXED_VALUE_FOR_ROUNDED_TIME = 1;
var PRECISION_STRING_LENGTH = 8;
export var getToFixedValue = function getToFixedValue(value, precision) {
  var _value$toString$split = value.toString().split('.'),
      _value$toString$split2 = _slicedToArray(_value$toString$split, 2),
      number = _value$toString$split2[0],
      _value$toString$split3 = _value$toString$split2[1],
      decimal = _value$toString$split3 === void 0 ? '0' : _value$toString$split3;

  return decimal ? "".concat(number, ".").concat(_padEnd(decimal.substr(0, precision), precision, '0')) : number;
};
export var convertSecToHours = function convertSecToHours(seconds) {
  var _ref5 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : EMPTY_OBJECT,
      shouldRoundOff = _ref5.shouldRoundOff,
      hoursRoundOff = _ref5.hoursRoundOff;

  if (!seconds) return '0.00';
  var roundOffThreshold = DEFAULT_ROUND_OFF_THRESHOLD - ROUND_OFF_PRECISION * (hoursRoundOff || 5);
  var time = seconds / ONE_HOUR_TO_SECONDS || 0;
  if (!shouldRoundOff && !hoursRoundOff) return getToFixedValue(time, DECIMAL_FIXED_VALUE_TIME);
  var timeWithRoundOff = time + roundOffThreshold;
  return _toNumber(getToFixedValue(time + roundOffThreshold, DECIMAL_FIXED_VALUE_TIME)) < _toNumber(timeWithRoundOff.toFixed(DECIMAL_FIXED_VALUE_TIME)) ? timeWithRoundOff.toFixed(DECIMAL_FIXED_VALUE_TIME) : getToFixedValue(time, DECIMAL_FIXED_VALUE_TIME);
};
export var convertSecToMins = function convertSecToMins(seconds) {
  var _ref6 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : EMPTY_OBJECT,
      shouldRoundOff = _ref6.shouldRoundOff,
      hoursRoundOff = _ref6.hoursRoundOff;

  if (!seconds) return '0.00';
  var roundOffThreshold = DEFAULT_ROUND_OFF_THRESHOLD - ROUND_OFF_PRECISION * (hoursRoundOff || 5);
  var time = seconds / ONE_MINUTE_TO_SECONDS || 0;
  if (!shouldRoundOff && !hoursRoundOff) return getToFixedValue(time, DECIMAL_FIXED_VALUE_TIME);
  var timeWithRoundOff = time + roundOffThreshold;
  return _toNumber(getToFixedValue(time + roundOffThreshold, DECIMAL_FIXED_VALUE_TIME)) < _toNumber(timeWithRoundOff.toFixed(DECIMAL_FIXED_VALUE_TIME)) ? timeWithRoundOff.toFixed(DECIMAL_FIXED_VALUE_TIME) : getToFixedValue(time, DECIMAL_FIXED_VALUE_TIME);
};

var getParsedSeconds = function getParsedSeconds(hour) {
  return _parseInt(Numeral(hour || 0).multiply(ONE_HOUR_TO_SECONDS).value(), 10);
};

export var getCustomRoundOffPrecision = function getCustomRoundOffPrecision() {
  var precision = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
  var precisionStringLength = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : PRECISION_STRING_LENGTH;
  var returnString = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;

  // Generate the decimal value as a string
  var ones = _repeat('1', precisionStringLength - precision);

  var zeroes = _repeat('0', precision); // Generate the precision value as a string


  var precisionString = "0.".concat(_join([zeroes, ones], EMPTY_STRING));
  if (returnString) return precisionString;
  return _toNumber(precisionString);
};
export var convertHoursToSec = function convertHoursToSec(hour) {
  var _ref7 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : EMPTY_OBJECT,
      shouldRoundOff = _ref7.shouldRoundOff,
      hoursRoundOff = _ref7.hoursRoundOff;

  if (_isNaN(_parseInt(hour))) return 0;
  if (!shouldRoundOff && !hoursRoundOff) return getParsedSeconds(hour);
  var roundOffPrecision = getCustomRoundOffPrecision(DECIMAL_FIXED_VALUE_FOR_ROUNDED_TIME, 2);
  var roundOffThreshold = mathUtils.subtract(mathUtils.multiply(roundOffPrecision, 5), mathUtils.multiply(roundOffPrecision, hoursRoundOff + DECIMAL_FIXED_VALUE_FOR_ROUNDED_TIME));
  var time = hour || 0;
  var timeWithRoundOffThreshold = mathUtils.add(time, roundOffThreshold);
  return getParsedSeconds(_round(timeWithRoundOffThreshold, DECIMAL_FIXED_VALUE_FOR_ROUNDED_TIME));
};
export var convertMinsToSec = function convertMinsToSec(minute) {
  if (_isNaN(_parseInt(minute))) return 0;
  return parseInt(Numeral(minute || 0).multiply(ONE_MINUTE_TO_SECONDS).value(), 10);
};
export var getDateDifferenceHumanised = function getDateDifferenceHumanised(date) {
  return "".concat(moment.duration(moment().diff(date)).humanize(), " ago");
};
export var getMonthFromNumber = function getMonthFromNumber(number) {
  return moment().month(number).format('MMMM');
};
export var disabledDate = _curry(function (options, current) {
  var activeYear = options.activeYear,
      activeMonth = options.activeMonth;
  return current.month() < activeMonth - 1 && current.year() <= activeYear;
});
export var getPastDateFromDuration = function getPastDateFromDuration(duration) {
  var durationUnitIn = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'ms';
  return getTimeStamp() - moment.duration(duration, durationUnitIn).valueOf();
};
export var timeSince = function timeSince(date) {
  if (date) {
    return moment(date).fromNow();
  }

  return undefined;
};
export var isMomentValid = function isMomentValid(value) {
  return moment.isMoment(value) && value.isValid();
};
export var getStartOfMonth = function getStartOfMonth(value) {
  return toMoment(value).startOf('month').valueOf();
}; // return start of the month in epoch

export var getStartOfYearEpoch = function getStartOfYearEpoch(value) {
  return toMoment(value).startOf('year').valueOf();
}; // return start of the year in epoch

export var getEndOfMonth = function getEndOfMonth(value) {
  return toMoment(value).endOf('month').valueOf();
}; // return end of the month in epoch

export var getEndOfYearEpoch = function getEndOfYearEpoch(value) {
  return toMoment(value).endOf('year').valueOf();
}; // return end of the year in epoch

export var toMomentDuration = function toMomentDuration(duration) {
  var durationUnit = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'ms';
  return moment.duration(duration, durationUnit);
};
export var getTimeByFormat = function getTimeByFormat(time, format) {
  return moment(time, format);
}; // Today, Tommorrow, Yesterday else actual Data

export var RELATIVE_DATE_FORMAT = {
  sameDay: '[Today]',
  nextDay: '[Tomorrow]',
  lastDay: '[Yesterday]',
  lastWeek: '[Last] dddd',
  nextWeek: DATE_TIME_FORMATS.DATE_ALPHA_MONTH,
  sameElse: DATE_TIME_FORMATS.DATE_ALPHA_MONTH
};
export var RELATIVE_DATE_FORMAT_WITH_TIME = {
  sameDay: '[Today] [at] hh:mm A',
  nextDay: '[Tomorrow] [at] hh:mm A',
  lastDay: '[Yesterday] [at] hh:mm A',
  lastWeek: '[Last] dddd [at] hh:mm A',
  nextWeek: DATE_TIME_FORMATS.DATE_ALPHA_MONTH,
  sameElse: 'MMM DD, YYYY [at] hh:mm A'
};
export var RELATIVE_DATE_FORMAT_WITH_TIME_IN_MM_DD_YYYY = _objectSpread(_objectSpread({}, RELATIVE_DATE_FORMAT_WITH_TIME), {}, {
  sameElse: 'MM/DD/YYYY [at] hh:mm A'
});
export var getCalendarDateDisplay = function getCalendarDateDisplay(epochOrMomentObject) {
  var formats = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : RELATIVE_DATE_FORMAT;
  return moment(epochOrMomentObject).calendar(null, formats);
};
export var nextday = function nextday(fromDate) {
  return moment(fromDate).add(1, 'day').valueOf();
};
export var getLocalTimeZone = function getLocalTimeZone() {
  return moment.tz.guess(true);
};
export var getCurrentYear = function getCurrentYear() {
  return getCurrentTime().format(DATE_TIME_FORMATS.YEAR);
};
export var getCurrentYearValue = function getCurrentYearValue() {
  return toMoment().year();
};
export var getCurrentQuarter = function getCurrentQuarter() {
  return toMoment().quarter();
};
/**
 * returns {month, year} from momentObject
 * @param {any} momentObject is a momentObject
 * @return {month, year}
 */

export var getYearMonthFromMonthMoment = function getYearMonthFromMonthMoment(momentObject) {
  var month = momentObject.month();
  var year = momentObject.year();
  return {
    year: year,
    month: month
  };
};
export var getNumberOfDaysInCurrentMonth = function getNumberOfDaysInCurrentMonth() {
  return moment().daysInMonth();
};
export var getNextHour = function getNextHour() {
  var value = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : toMoment();
  return value.add(1, 'hour');
};
/*
 * @param {any} date can be a momentObject or a string
 * @returns day of the week in number ex: 1 for monday, 2 for tuesday
 */

export var getWeekDay = function getWeekDay(date) {
  return toMoment(date).weekday();
};
/**
 * returns startOfDay/endOfDay in unix timestamp
 */

export var startOfWeek = function startOfWeek(time) {
  return moment(time).startOf('week');
};
export var endOfWeek = function endOfWeek(time) {
  return moment(time).endOf('week');
};
/*
 * Adds the provided numberOfweeks to the provided date or defaults to today.
 * @param {any} numberOfWeeksToAdd number of weeks to add
 * @param {any} startDate can be a momentObject or a string
 * @returns a moment object
 */

export var addWeeks = function addWeeks(numberOfWeeksToAdd, startDate) {
  var start = startDate && toMoment(startDate) || getToday();
  return start.add(numberOfWeeksToAdd, 'weeks');
};
export var addHours = function addHours(hours, startDate) {
  var start = startDate && toMoment(startDate) || getToday();
  return start.add(hours, 'hours');
};
export var subtractHours = function subtractHours(hours, startDate) {
  var start = startDate && toMoment(startDate) || getToday();
  return start.subtract(hours, 'hours');
};
export var addMinutes = function addMinutes(minutes, startDate) {
  var start = startDate && toMoment(startDate) || getToday();
  return start.add(minutes, 'minutes');
};
export var addSeconds = function addSeconds(seconds, startDate) {
  var start = startDate && toMoment(startDate) || getToday();
  return start.add(seconds, 'seconds');
};
export var addMonths = function addMonths(months, startDate) {
  var start = startDate && toMoment(startDate) || getToday();
  return start.add(months, 'months');
};
export var addYears = function addYears(years, startDate) {
  var start = startDate && toMoment(startDate) || getToday();
  return start.add(years, 'years');
};
export var millisecondsToMinutes = function millisecondsToMinutes(timeInMilliseconds) {
  return moment.duration(timeInMilliseconds, 'milliseconds').asMinutes();
};
export var millisecondsToSeconds = function millisecondsToSeconds(timeInMilliseconds) {
  return moment.duration(timeInMilliseconds, 'milliseconds').asSeconds();
};
export var millisecondsToDays = function millisecondsToDays(timeInMilliseconds) {
  return moment.duration(timeInMilliseconds, 'milliseconds').asDays();
};
export var millisecondsToHours = function millisecondsToHours(timeInMilliseconds) {
  return moment.duration(timeInMilliseconds, 'milliseconds').asHours();
};
export var getDurationInMilliseconds = function getDurationInMilliseconds(value) {
  var durationPeriod = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'seconds';
  return moment.duration(value, durationPeriod).asMilliseconds();
};
export var getDurationFromMilliSeconds = function getDurationFromMilliSeconds(milliseconds) {
  var durationInMilliSeconds = _isString(milliseconds) ? _toNumber(milliseconds) : milliseconds;
  if (!_isNumber(durationInMilliSeconds)) return milliseconds;
  if (durationInMilliSeconds <= 999) return __('0sec');
  var unAccountedDuration = durationInMilliSeconds;
  var durationPeriodGetterList = [[millisecondsToDays, getDurationInMilliseconds, 'days', __('day')], [millisecondsToHours, getDurationInMilliseconds, 'hours', __('hr')], [millisecondsToMinutes, getDurationInMilliseconds, 'minutes', __('min')], [millisecondsToSeconds, getDurationInMilliseconds, undefined, __('sec')]];
  return _flow(_reduce, _trim)(durationPeriodGetterList, function (acc, _ref8) {
    var _ref9 = _slicedToArray(_ref8, 4),
        getDurationPeriod = _ref9[0],
        getMillisecOfDurationPeriod = _ref9[1],
        durationPeriod = _ref9[2],
        durationPeriodDisplay = _ref9[3];

    var subDuration = _flow(getDurationPeriod, _toInteger)(unAccountedDuration);

    if (!subDuration) return acc;
    unAccountedDuration -= getMillisecOfDurationPeriod(subDuration, durationPeriod);
    return "".concat(acc, " ").concat(subDuration).concat(durationPeriodDisplay);
  }, EMPTY_STRING);
};
export var isSameAsCurrentMonth = function isSameAsCurrentMonth(selectedMonthEpoch) {
  var startOfCurrentMonth = getStartOfMonth();
  var startOfSelectedMonth = getStartOfMonth(selectedMonthEpoch);
  return startOfCurrentMonth === startOfSelectedMonth;
};
/**
 * returns week days in string
 */

export var getWeekDays = function getWeekDays() {
  return moment.weekdays();
};
/**
 * returns week days in string in short form
 */

export var getWeekDaysShort = function getWeekDaysShort() {
  return moment.weekdaysShort();
};
/**
 *
 * @param {any} date : takes in an input for next date.
 * @returns the next sunday date
 * This function basically returns date of the next sunday, used for calculating the current bi weekly range
 */

export var endOfNextWeek = function endOfNextWeek(date) {
  return addWeeks(1, endOfWeek(date));
};
/**
 * returns value for end of the month for that year
 */

export var getEndOfMonthOfYear = function getEndOfMonthOfYear(year, month) {
  return +toMoment().year(year).month(month).endOf('month');
};
/**
 * returns value for last date of current month and year;
 */

export var getLastDayOfCurrentMonthMoment = function getLastDayOfCurrentMonthMoment() {
  return toMoment().endOf('month');
};
/**
 * This function basically returns range of epoch from current month to current date
 * @returns {Array}
 */

export var getMonthToDateRangeEpoch = function getMonthToDateRangeEpoch() {
  return [getStartOfMonth(), getTodayTimeStamp()];
};
/**
 *
 * @param {any} start : { time, value }
 * @param {any} end :  { time, value }
 * time can be year / month / week / isoWeek / day  / hour / minute / second
 * value can be epoch / moment obj
 * @returns {Array}
 * This function basically returns range of epoch for given time
 */

export var getRangeEpoch = function getRangeEpoch() {
  var start = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var end = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var startEpoch = getStartEpoch(start.time, start.value);
  var endEpoch = getEndEpoch(end.time, end.value);
  return [startEpoch, endEpoch];
};
/**
 *
 * @param {any} value : can be epoch / moment obj
 * @param {any} time : can be year / month / week / isoWeek / day  / hour / minute / second
 * @returns the epoch
 * This function basically returns epoch for start time of the given period
 */

export var getStartEpoch = function getStartEpoch() {
  var time = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'day';
  var value = arguments.length > 1 ? arguments[1] : undefined;
  return toMoment(value).startOf(time).valueOf();
};
/**
 *
 * @param {any} value : can be epoch / moment obj
 * @param {any} time : can be year / month / week / isoWeek / day  / hour / minute / second
 * @returns the epoch
 * This function basically returns epoch for end time of the given period
 */

export var getEndEpoch = function getEndEpoch() {
  var time = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'day';
  var value = arguments.length > 1 ? arguments[1] : undefined;
  return toMoment(value).endOf(time).valueOf();
};
export var getStartOfDayEpoch = _flow([startOfDay, getUnix]);
export var getEndOfDayEpoch = _flow([endOfDay, getUnix]);
export var getAbbreviatedMonthFromNumber = function getAbbreviatedMonthFromNumber(number) {
  return toMoment().month(number).format(DATE_TIME_FORMATS.ABBREVIATED_MONTH);
};
export var getPreviousWeekStartAndEnd = function getPreviousWeekStartAndEnd() {
  return [toMoment().subtract(1, 'weeks').startOf('week'), toMoment().subtract(1, 'weeks').endOf('week')];
};
export var getLastSevenDaysRange = function getLastSevenDaysRange() {
  return [toMoment().subtract(7, 'd').startOf(), toMoment().endOf()];
};
export var getWeekToDateRange = function getWeekToDateRange() {
  return [startOfDay(toMoment().startOf('week')), endOfDay(toMoment())];
};
export var getLastOneMonth = function getLastOneMonth() {
  return [toMoment().subtract(1, 'M').startOf('day'), endOfDay(toMoment())];
};
export var addHourMinutesToDate = function addHourMinutesToDate(timeMoment, date) {
  var startOfDate = _isNil(date) ? startOfDay() : date;
  var min = toMoment(timeMoment).minutes();
  var hour = toMoment(timeMoment).hour();
  return getUnix(toMoment(startOfDate).add(hour, 'hour').add(min, 'minutes'));
};
/**
 * Rounds up the provided timeMoment to the next nearest provided minute.
 * @param {any} timeMoment is a momentObject, defaults to moment object for the current date
 * @param {any} roundUptoMinute is a minute numeral, defaults to 1
 * @returns a momentObject
 */

export var roundUptoNextMinute = function roundUptoNextMinute(roundUptoMinute, timeMoment) {
  var roundUpto = _isNumber(roundUptoMinute) ? roundUptoMinute : 1;
  var timeEpoch = isValidDate(timeMoment) ? timeMoment.valueOf() : getToday().valueOf();
  var roundUptoMilliSeconds = mathUtils.multiply(mathUtils.multiply(roundUpto, ONE_MINUTE_TO_SECONDS), ONE_SECOND_TO_MILLISECONDS);
  var roundedTimeEpoch = roundNumberToNearestMultiple(timeEpoch, roundUptoMilliSeconds);
  return toMoment(roundedTimeEpoch);
};
export var toMomentWithEmptyCheck = function toMomentWithEmptyCheck(date) {
  return _isNil(date) ? undefined : toMoment(date);
};
export var getCurrentTimeUnix = function getCurrentTimeUnix() {
  return getUnix(getCurrentTime());
};
export { moment };